export class ExamplePostpositionTree {
  id: number;
  name: string;

  constructor(obj?: any) {
    this.id   = (obj && obj.id)   ?? null;
    this.name = (obj && obj.name) ?? null;
  }
}

const EXAMPLE_CAT_INDEX_CHUNK_SIZE = 12;

function getCatIndexKey(catId: number, chunkIndex: number, chunkCount: number): string {
  if (chunkCount <= 1) {
    return String(catId);
  }

  return `${catId}${String.fromCharCode('a'.charCodeAt(0) + chunkIndex)}`;
}

export class ExampleCatIndexItem {
  key: string;
  catId: number;
  name: string;
  sourceName: string;
  postpositions: Array<ExamplePostpositionTree>;
  chunkIndex: number;
  chunkCount: number;

  constructor(
    cat: ExampleCatTree,
    postpositions: Array<ExamplePostpositionTree>,
    chunkIndex: number,
    chunkCount: number,
  ) {
    this.key = getCatIndexKey(cat.id, chunkIndex, chunkCount);
    this.catId = cat.id;
    this.sourceName = cat.name;
    this.name = chunkCount > 1 ? `${cat.name} (${chunkIndex + 1})` : cat.name;
    this.postpositions = postpositions;
    this.chunkIndex = chunkIndex;
    this.chunkCount = chunkCount;
  }
}

export function getExampleCatIdFromIndexKey(catKey: unknown): number | null {
  if (catKey === null || catKey === undefined) {
    return null;
  }

  const match = String(catKey).match(/^(\d+)/);
  return match ? Number(match[1]) : null;
}

export function buildExampleCatIndexItems(treeList: Array<ExampleCatTree>): Array<ExampleCatIndexItem> {
  return treeList.flatMap(cat => {
    const chunkCount = Math.ceil(cat.postpositions.length / EXAMPLE_CAT_INDEX_CHUNK_SIZE);

    if (chunkCount <= 1) {
      return [new ExampleCatIndexItem(cat, cat.postpositions, 0, 1)];
    }

    return Array.from({length: chunkCount}, (_, chunkIndex) => {
      const start = chunkIndex * EXAMPLE_CAT_INDEX_CHUNK_SIZE;
      return new ExampleCatIndexItem(
        cat,
        cat.postpositions.slice(start, start + EXAMPLE_CAT_INDEX_CHUNK_SIZE),
        chunkIndex,
        chunkCount,
      );
    });
  });
}

export function findExampleCatIndexItemByKey(
  treeList: Array<ExampleCatTree>,
  catKey: unknown,
): ExampleCatIndexItem | null {
  const items = buildExampleCatIndexItems(treeList);
  const key = String(catKey);
  const exactItem = items.find(item => item.key === key);
  if (exactItem) {
    return exactItem;
  }

  const catId = getExampleCatIdFromIndexKey(catKey);
  const fallback = items.find(item => item.catId === catId) ?? null;
  if (fallback) {
    console.warn(
      `findExampleCatIndexItemByKey: requested key=${key} not found; falling back to key=${fallback.key}.`,
      { requestedKey: key, fallbackKey: fallback.key, chunkCount: fallback.chunkCount },
    );
  }
  return fallback;
}

export function findExampleCatIndexItemByPostposition(
  treeList: Array<ExampleCatTree>,
  postpositionId: number,
): {catItem: ExampleCatIndexItem, postposition: ExamplePostpositionTree} | null {
  for (const catItem of buildExampleCatIndexItems(treeList)) {
    const postposition = catItem.postpositions.find(pp => pp.id === postpositionId);
    if (postposition) {
      return {catItem, postposition};
    }
  }

  return null;
}

export class ExampleCatTree {
  id: number;
  name: string;
  postpositions: Array<ExamplePostpositionTree>;

  constructor(obj?: any) {
    this.id   = (obj && obj.id)   ?? null;
    this.name = (obj && obj.name) ?? null;

    this.postpositions = new Array<ExamplePostpositionTree>();
    if (obj?.postpositions && Array.isArray(obj.postpositions)) {
      obj.postpositions.forEach((value: any) =>
        this.postpositions.push(new ExamplePostpositionTree(value))
      );
    }
  }
}
